import type { LibraryEntryResponse, ParameterLibraryType } from '@malaberg/shared';
import { Combobox, Group, Input, Text, useCombobox } from '@mantine/core';
import { notifications } from '@mantine/notifications';
/**
 * LibraryCombobox — T23 + T6.
 *
 * Generic combobox for library-backed fields: angle, avatar, problem_mechanism,
 * solution_mechanism.
 *
 * Behavior:
 * - Opens dropdown on focus/click.
 * - Filters entries by typed substring (case-insensitive).
 * - When typed value has no exact match AND canAddNew=true: shows AddNewConfirmRow.
 * - Clicking add-new:
 *   1. Disables the row (prevents double-submit).
 *   2. Calls librariesApi.create(type, { scope, value: sanitize(typed), displayName: typed, metadata: {} }).
 *   3. On success: refetches list, selects new entry via onChange(newEntryId).
 *   4. On 409 duplicate_value: selects existing matching entry instead.
 *   5. On other errors: shows notification toast.
 *
 * T6 — Ephemeral mode (when fieldValue.kind === 'ephemeral'):
 * - Input shows ephemeralValue text.
 * - A subtle "free text" badge is shown next to the input.
 * - Add-new row is hidden (the value was AI-applied, not user-typed).
 * - Typing transitions the field to {kind:'value'} via onFieldChange.
 * - Clicking a dropdown item transitions to {kind:'entry'} via onFieldChange.
 *
 * Exports `sanitize` for testing: lower-snake-cases a display name.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { librariesApi, unwrapLibraryEntryResponse } from '../../api/endpoints/libraries.js';
import { ApiError } from '../../api/errors.js';
import { useLibraries } from '../../hooks/use-libraries.js';
import { useBriefFormStore } from '../../state/brief-form-store.js';
import type { FieldValue } from '../../state/brief-form-store.js';
import { AddNewConfirmRow } from './AddNewConfirmRow.js';

// ─── Exported for testing ─────────────────────────────────────────────────────

/**
 * Converts a display name to a lower_snake_case library value key.
 * Matches the backend sanitize function: toLowerCase + non-alnum → '_'.
 */
export function sanitize(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9_]/g, '_');
}

function isHttpUrl(text: string): boolean {
  try {
    const url = new URL(text);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

// ─── Internal constants ───────────────────────────────────────────────────────

/** Sentinel value used as the Combobox option value for the add-new row */
const ADD_NEW_VALUE = '__add_new__';

// ─── Component ────────────────────────────────────────────────────────────────

export interface LibraryComboboxProps {
  type: ParameterLibraryType;
  label: string;
  placeholder: string;
  /** Current selected entry ID (null = nothing selected). Used for backward compat with BriefForm. */
  value: string | null;
  onChange: (entryId: string | null) => void;
  /**
   * T6: 3-channel field state. When provided, takes precedence over `value` for
   * display and indicator purposes. `onFieldChange` is called on transitions.
   */
  fieldValue?: FieldValue | null;
  /** T6: Called when the field channel changes (ephemeral → value, any → entry). */
  onFieldChange?: (fv: FieldValue | null) => void;
  /** Whether to show the "+ Add new" affordance */
  canAddNew: boolean;
  'data-testid'?: string;
}

export function LibraryCombobox({
  type,
  label,
  placeholder,
  value,
  onChange,
  fieldValue,
  onFieldChange,
  canAddNew,
  'data-testid': testId,
}: LibraryComboboxProps) {
  const store = useCombobox({
    onDropdownClose: () => store.resetSelectedOption(),
  });

  const productId = useBriefFormStore((s) => s.productId);
  const scope = productId ? `product:${productId}` : null;
  const isLandingPageUrl = type === 'landing_page_url';

  const { entries, refetch } = useLibraries(type, scope);

  const [inputValue, setInputValue] = useState('');
  const [hasEditedInput, setHasEditedInput] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cancel any pending blur timer on unmount to avoid state-after-unmount errors
  useEffect(() => {
    return () => {
      if (blurTimerRef.current !== null) {
        clearTimeout(blurTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!fieldValue && !value) {
      setInputValue('');
      setHasEditedInput(false);
    } else if (fieldValue?.kind === 'ephemeral') {
      setInputValue('');
      setHasEditedInput(false);
    }
  }, [fieldValue, value]);

  // ── Ephemeral mode detection ────────────────────────────────────────────────
  const isEphemeral = !hasEditedInput && fieldValue?.kind === 'ephemeral';
  const isValueField = !hasEditedInput && fieldValue?.kind === 'value';

  // ── Derive display text from selected value or fieldValue ──────────────────
  const selectedEntry = entries.find((e) => e.id === value) ?? null;

  // When in ephemeral mode, show the ephemeralValue text in the input.
  // When an entry is selected via the old prop, show its displayName.
  // Otherwise show what the user has typed.
  let displayText: string;
  if (hasEditedInput) {
    displayText = inputValue;
  } else if (isEphemeral) {
    displayText = (fieldValue as { kind: 'ephemeral'; ephemeralValue: string }).ephemeralValue;
  } else if (isValueField) {
    displayText = (fieldValue as { kind: 'value'; value: string }).value;
  } else if (value && selectedEntry) {
    displayText = selectedEntry.displayName;
  } else {
    displayText = inputValue;
  }

  // ── Filter entries by typed substring ─────────────────────────────────────
  // In ephemeral mode, we don't filter (the ephemeral text isn't a search query).
  const searchText = isEphemeral ? '' : displayText;
  const query = searchText.toLowerCase();
  const filtered = query
    ? entries.filter((e) => e.displayName.toLowerCase().includes(query))
    : entries;

  // ── Exact match check (case-insensitive) ──────────────────────────────────
  const exactMatch = entries.find(
    (e) => e.displayName.toLowerCase() === searchText.trim().toLowerCase()
  );
  const hasExactMatch = exactMatch !== undefined;

  // ── Show add-new row when: user typed something (not ephemeral), no exact match, canAddNew ─
  const trimmedSearch = searchText.trim();
  const canAddTypedValue = !isLandingPageUrl || isHttpUrl(trimmedSearch);
  const showAddNew =
    canAddNew && !isEphemeral && trimmedSearch.length > 0 && !hasExactMatch && canAddTypedValue;

  // ── Handle option selection ────────────────────────────────────────────────
  async function handleOptionSubmit(optionValue: string) {
    if (optionValue === ADD_NEW_VALUE) {
      await handleAddNew();
      return;
    }

    // Select existing entry
    const entry = entries.find((e) => e.id === optionValue);
    if (entry) {
      setInputValue('');
      setHasEditedInput(false);
      onChange(entry.id);
      // T6: also emit the rich FieldValue to the new channel
      onFieldChange?.({ kind: 'entry', entryId: entry.id });
      store.closeDropdown();
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  }

  const handleAddNew = useCallback(async () => {
    if (isAdding || !scope) return;

    const typed = inputValue.trim();
    if (!typed) return;
    if (isLandingPageUrl && !isHttpUrl(typed)) return;

    setIsAdding(true);

    try {
      const res = await librariesApi.create(type, {
        scope,
        displayName: typed,
        ...(isLandingPageUrl ? {} : { value: sanitize(typed) }),
        metadata: {},
      });

      const created = unwrapLibraryEntryResponse(res);
      await refetch();
      setInputValue('');
      setHasEditedInput(false);
      onChange(created.id);
      onFieldChange?.({ kind: 'entry', entryId: created.id });
      store.closeDropdown();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'duplicate_value') {
        // Select the existing entry that matches
        const sanitized = sanitize(typed);
        const existing = entries.find(
          (e) => e.value === sanitized || e.displayName.toLowerCase() === typed.toLowerCase()
        );
        if (existing) {
          setInputValue('');
          setHasEditedInput(false);
          onChange(existing.id);
          onFieldChange?.({ kind: 'entry', entryId: existing.id });
          store.closeDropdown();
        }
      } else if (err instanceof ApiError && err.status === 403) {
        notifications.show({
          title: 'Permission denied',
          message: 'Awareness levels are managed by admins.',
          color: 'red',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to add entry',
          color: 'red',
        });
      }
    } finally {
      setIsAdding(false);
    }
  }, [
    entries,
    inputValue,
    isAdding,
    isLandingPageUrl,
    onChange,
    onFieldChange,
    refetch,
    scope,
    store,
    type,
  ]);

  // Ref to the underlying input so we can imperatively blur it after a
  // selection — without this, single-select dropdowns stay visible until the
  // user clicks elsewhere.
  const inputRef = useRef<HTMLInputElement>(null);

  // ── Handle input change ────────────────────────────────────────────────────
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.currentTarget.value;
    setInputValue(val);
    setHasEditedInput(true);

    const trimmed = val.trim();
    const matchingEntry = entries.find(
      (e) => e.displayName.toLowerCase() === trimmed.toLowerCase()
    );
    if (!trimmed) {
      onChange(null);
      onFieldChange?.(null);
    } else if (matchingEntry) {
      onChange(matchingEntry.id);
      onFieldChange?.({ kind: 'entry', entryId: matchingEntry.id });
    } else {
      // Non-matching typed text is valid free text for run creation.
      if (value) {
        onChange(null);
      }
      onFieldChange?.({ kind: 'value', value: val });
    }

    store.openDropdown();
    store.updateSelectedOptionIndex();
  };

  return (
    <Combobox
      store={store}
      onOptionSubmit={(optVal) => {
        void handleOptionSubmit(optVal);
      }}
    >
      <Combobox.Target>
        <Input.Wrapper label={label}>
          {/* T6: Subtle "free text" indicator when field is in ephemeral state */}
          {isEphemeral && (
            <Group gap={4} mb={2}>
              <Text size="xs" c="dimmed" fs="italic" data-testid="ephemeral-indicator">
                free text
              </Text>
            </Group>
          )}
          <Input
            ref={inputRef}
            component="input"
            placeholder={placeholder}
            data-testid={testId}
            value={displayText}
            onChange={handleInputChange}
            onFocus={() => store.openDropdown()}
            onBlur={() => {
              // Small delay so option clicks register before blur closes dropdown
              blurTimerRef.current = setTimeout(() => store.closeDropdown(), 150);
            }}
            onClick={() => store.openDropdown()}
          />
        </Input.Wrapper>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {filtered.length === 0 && !showAddNew && (
            <Combobox.Empty>
              <Text size="sm" c="dimmed">
                No entries
              </Text>
            </Combobox.Empty>
          )}

          {filtered.map((entry) => (
            <Combobox.Option key={entry.id} value={entry.id}>
              {entry.displayName}
            </Combobox.Option>
          ))}

          {showAddNew && (
            <AddNewConfirmRow
              typedValue={inputValue.trim()}
              typeName={type.replace(/_/g, ' ')}
              optionValue={ADD_NEW_VALUE}
              disabled={isAdding}
            />
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

// Re-export entry type for consumers
export type { LibraryEntryResponse };
